/**
 * RevenueCat Web Billing wrapper.
 *
 * We lazy-load `@revenuecat/purchases-js` so users who never open the
 * paywall don't pay the bundle cost.
 *
 * Flow:
 *   1. After auth: configureRevenueCat(user.user_id) sets the app user id
 *   2. Open paywall:    fetchOfferings() returns packages
 *   3. User taps a plan: purchasePackage(pkg) launches RC-hosted Stripe checkout
 *   4. After payment, RevenueCat webhook syncs to our MongoDB; the
 *      subscription_status endpoint will see the new tier
 */

const PUBLIC_API_KEY = process.env.REACT_APP_REVENUECAT_PUBLIC_API_KEY;

let purchasesModulePromise = null;
let configured = false;
let configuredUserId = null;
let configurePromise = null;  // Resolves when Purchases.configure() has been called

/** Lazy-load the SDK once. Memoized. */
async function getPurchases() {
    if (!purchasesModulePromise) {
        purchasesModulePromise = import('@revenuecat/purchases-js').then(m => m.Purchases);
    }
    return purchasesModulePromise;
}

/**
 * Configure RevenueCat with the current user's id.
 * Safe to call multiple times - it's a no-op if already configured for the same user.
 */
export async function configureRevenueCat(userId, attributes = null) {
    if (!PUBLIC_API_KEY) {
        console.warn('[RC] REACT_APP_REVENUECAT_PUBLIC_API_KEY missing');
        return;
    }
    if (!userId) return;
    if (configured && configuredUserId === userId) {
        // Even when already configured, allow updating attributes (email/name).
        // All RC calls are async - we wrap each one in `.catch()` so the
        // dev-overlay error boundary doesn't surface their internal
        // failures (RC throws non-fatal "Unknown backend error" if the
        // user attribute service is briefly unreachable, the user's
        // session isn't yet known to RC, etc.). These are entirely safe
        // to swallow - RC retries internally.
        if (attributes) {
            try {
                const Purchases = await getPurchases();
                const inst = Purchases.getSharedInstance?.() ?? Purchases;
                if (typeof inst.setAttributes === 'function') {
                    Promise.resolve(inst.setAttributes(attributes)).catch(() => {});
                }
                if (attributes.$email && typeof inst.setEmail === 'function') {
                    Promise.resolve(inst.setEmail(attributes.$email)).catch(() => {});
                }
                if (attributes.$displayName && typeof inst.setDisplayName === 'function') {
                    Promise.resolve(inst.setDisplayName(attributes.$displayName)).catch(() => {});
                }
            } catch (_) { /* silent */ }
        }
        return;
    }

    configurePromise = (async () => {
        try {
            const Purchases = await getPurchases();
            if (typeof Purchases.configure === 'function') {
                Purchases.configure(PUBLIC_API_KEY, userId);
            } else if (typeof Purchases.configureWithApiKey === 'function') {
                Purchases.configureWithApiKey(PUBLIC_API_KEY, userId);
            }
            configured = true;
            configuredUserId = userId;
            // After configure, push attributes so RC can identify the user.
            // Same async-rejection handling as above.
            if (attributes) {
                try {
                    const inst = Purchases.getSharedInstance?.() ?? Purchases;
                    if (typeof inst.setAttributes === 'function') {
                        Promise.resolve(inst.setAttributes(attributes)).catch(() => {});
                    }
                    if (attributes.$email && typeof inst.setEmail === 'function') {
                        Promise.resolve(inst.setEmail(attributes.$email)).catch(() => {});
                    }
                    if (attributes.$displayName && typeof inst.setDisplayName === 'function') {
                        Promise.resolve(inst.setDisplayName(attributes.$displayName)).catch(() => {});
                    }
                } catch (_) { /* silent */ }
            }
        } catch (err) {
            // Surfacing this would block the whole app - but we still want
            // visibility in dev. Use console.warn (not error) so it doesn't
            // trip the dev error overlay.
            console.warn('[RC] configure failed', err);
        }
    })();
    await configurePromise;
}

/**
 * Block until RC is configured. If configureRevenueCat() was never called
 * (e.g. user opened the paywall before AuthContext finished), fall back to
 * configuring with an anonymous id so we can at least load offerings.
 */
async function waitForConfigured() {
    if (configured) return;
    if (configurePromise) {
        await configurePromise;
        if (configured) return;
    }
    // Last-resort: configure anonymously so getOfferings() won't throw.
    // RC auto-generates an anonymous id that we'll later merge into the real
    // user via logIn() once authentication completes.
    try {
        const Purchases = await getPurchases();
        if (typeof Purchases.configure === 'function') {
            Purchases.configure(PUBLIC_API_KEY);
            configured = true;
        }
    } catch (err) {
        console.warn('[RC] anonymous configure failed', err);
    }
}

/** Returns the current offering's packages, or [] on error. */
export async function fetchOfferings() {
    if (!PUBLIC_API_KEY) return [];
    try {
        await waitForConfigured();
        const Purchases = await getPurchases();
        const sharedInstance = Purchases.getSharedInstance?.() ?? Purchases;
        const offerings = await sharedInstance.getOfferings();
        const current = offerings?.current;
        if (!current) return [];
        // Different SDK versions structure the result differently - handle both
        const pkgs =
            current.availablePackages ||
            current.packages ||
            (current.all ? Object.values(current.all) : []);
        return pkgs || [];
    } catch (err) {
        console.error('[RC] fetchOfferings failed', err);
        return [];
    }
}

/**
 * Trigger purchase for a specific package. Redirects (or shows a Stripe
 * popup, depending on RC's mode) to complete payment.
 *
 * Returns { success: true } on completion, or throws on error.
 */
export async function purchasePackage(pkg) {
    if (!pkg) throw new Error('No package provided');
    const Purchases = await getPurchases();
    const sharedInstance = Purchases.getSharedInstance?.() ?? Purchases;

    const result = await sharedInstance.purchase({
        rcPackage: pkg,
    });

    return { success: true, result };
}

/** Tell RevenueCat the current user logged out. */
export async function logoutRevenueCat() {
    if (!configured) return;
    try {
        const Purchases = await getPurchases();
        const sharedInstance = Purchases.getSharedInstance?.() ?? Purchases;
        await sharedInstance.logOut?.();
    } catch (_) { /* silent */ }
    configured = false;
    configuredUserId = null;
}
